import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "./Displacement.css";
import { scaleLinear } from "d3-scale";
import bostonZipData from "../../../data/boston_zip.json";
import populationData from "../../../data/fig3_pop_pct_zip.json"; // Ensure this path is correct

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN as string;

interface PopulationYear {
  asian: number;
  black: number;
  hispanic: number;
  "native-american": number;
  white: number;
  other: number;
}

interface PopulationData {
  [zipCode: string]: {
    pop_2010: PopulationYear;
    pop_2020: PopulationYear;
  };
}

const DisplacementMap: React.FC<{
  year: "pop_2010" | "pop_2020";
  demographic: keyof PopulationYear;
}> = ({ year, demographic }) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // Define the color scale for the polygons on the map.
  const colorScale = scaleLinear<string>()
    .domain([0, 100])
    .range(["#EDE7F6", "#7B1FA2"]);

  // Render the map on mount (and only on mount).
  useEffect(() => {
    mapRef.current = new mapboxgl.Map({
      container: `disp-map-${year}`,
      style: "mapbox://styles/mapbox/dark-v10",
      center: [-71.057, 42.312],
      zoom: 10,
    });

    mapRef.current.on("load", () => {
      const plotted_zip_codes = new Set<string>();
      bostonZipData.features.forEach((feature) => {
        // Extract the zip code from the feature
        const zip = feature.properties.ZIP5;
        // Ensure there are no duplicate layers
        if (plotted_zip_codes.has(zip) || !populationData.hasOwnProperty(zip)) {
          return;
        }
        plotted_zip_codes.add(zip);

        // Extract the population data for the ZIP code
        const zipData = (populationData as PopulationData)[zip];
        if (!zipData) {
          // console.error(`No data for ZIP code: ${zip}`);
          return;
        }

        const demographicData = zipData[year][demographic];
        if (demographicData === undefined) {
          // console.error(`No ${demographic} data for year ${year} in ZIP code: ${zip}`);
          return;
        }

        const populationPercentage = demographicData / 100;

        // Fill layer for ZIP code regions
        mapRef.current!.addLayer({
          id: `${zip}-fill-${year}`,
          type: "fill",
          source: {
            type: "geojson",
            data: feature as GeoJSON.Feature,
          },
          paint: {
            "fill-color": colorScale(populationPercentage),
            "fill-opacity": 0.6,
          },
        });

        // Border layer for ZIP code regions
        mapRef.current!.addLayer({
          id: `${zip}-border-${year}`,
          type: "line",
          source: {
            type: "geojson",
            data: feature as GeoJSON.Feature,
          },
          paint: {
            "line-color": "#000000",
            "line-width": 1,
          },
        });

        // Label layer for ZIP code regions
        mapRef.current!.addLayer({
          id: `${zip}-label-${year}`,
          type: "symbol",
          source: {
            type: "geojson",
            data: feature as GeoJSON.Feature,
          },
          layout: {
            "text-field": `${populationPercentage}%`,
            "text-size": 12,
          },
        });
      });
    });

    // Disable scroll zoom by default.
    // Enable scroll zoom when clicking on the map, and disable it again
    // when clicking outside the map.
    mapRef.current!.scrollZoom.disable();
    const handleClick = (event: MouseEvent) => {
      const mapContainer = document.getElementById(`disp-map-${year}`);
      if (mapContainer && !mapContainer.contains(event.target as Node)) {
        mapRef.current!.scrollZoom.disable();
      } else {
        mapRef.current!.scrollZoom.enable();
      }
    };
    document.addEventListener("click", handleClick);

    return () => mapRef.current!.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update the fills on the map each time the filter is updated.
  useEffect(() => {
    if (mapRef.current && mapRef.current.isStyleLoaded()) {
      const plotted_zip_codes = new Set<string>();
      bostonZipData.features.forEach((feature) => {
        // Extract the zip code from the feature
        const zip = feature.properties.ZIP5;
        // Ensure there are no duplicate layers
        if (plotted_zip_codes.has(zip) || !populationData.hasOwnProperty(zip)) {
          return;
        }
        plotted_zip_codes.add(zip);

        // Extract the population data for the ZIP code
        const zipData = (populationData as PopulationData)[zip];
        if (!zipData) {
          return;
        }

        // Extract the demographic data for the ZIP code
        const demographicData = zipData[year][demographic];
        if (demographicData === undefined) {
          return;
        }

        const populationPercentage = demographicData / 100;

        mapRef.current!.setPaintProperty(
          `${zip}-fill-${year}`,
          "fill-color",
          colorScale(populationPercentage)
        );
        mapRef.current!.setLayoutProperty(
          `${zip}-label-${year}`,
          "text-field",
          `${populationPercentage}%`
        );
      });
    }
  });

  return (
    <div
      id={`disp-map-${year}`}
      className="displacement-map-container"
      style={{ height: "400px" }}
    />
  );
};

export default DisplacementMap;
